<!-- eslint-disable perfectionist/sort-modules -->
<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import range from 'lodash/range';
import {
  ChevronDown,
  ChevronUp,
  FunnelX,
  Plus,
  Search,
} from 'lucide-vue-next';
import {
  computed, onMounted, ref,
} from 'vue';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import ActionButton from '~/components/shared/ActionButton.vue';
import Pagination from '~/components/Pagination';
import useScrollToTop from '~/hooks/useScrollToTop';
import {
  DEFAULT_FALLBACK,
  formatBooleanValue,
  formatSkusValue,
  formatStringValue,
  formatValueWithUnit,
  UNITS,
} from '~/utils/formatters';
import {
  BUTTON_LABELS,
  ENABLED_OPTIONS,
  EQUIPMENT_BUTTON_LABELS,
  EQUIPMENT_EDIT_ROLE,
  EQUIPMENT_LABELS,
  FORM_PLACEHOLDERS,
  HYBRID_OPTIONS,
  MICROINVERTER_TABLE_COLUMNS,
  ORDER_BY_OPTIONS,
  PHASE_OPTIONS,
  STATUS_LABELS,
} from '~/constants/equipment';

import useTableSorting from '../hooks/useTableSorting';
import useMicroInvertersFilters from '../hooks/useMicroInvertersFilters';
import { useMicroInverters } from '../queries/useMicroInvertersCrud';
import MicroInverterModal from '../components/MicroInverterModal.vue';
import type { MicroInverters } from '~/types/equipment';

const TABLE_COLUMNS = MICROINVERTER_TABLE_COLUMNS;

const {
  apiParams,
  filterForm,
  handleReset,
  handleSearch,
  initializeForm,
  setPage,
  setPageSize,
} = useMicroInvertersFilters();

const [brandModel, brandProps] = filterForm.defineField('brand');
const [modelModel, modelProps] = filterForm.defineField('model');
const [skuModel, skuProps] = filterForm.defineField('sku');
const [powerModel, powerProps] = filterForm.defineField('power');
const [isEnabledModel, isEnabledProps] = filterForm.defineField('is_enabled');
const [isHybridModel, isHybridProps] = filterForm.defineField('is_hybrid');
const [orderByModel, orderByProps] = filterForm.defineField('order_by');

const {
  data: microInverters,
  isLoading: isLoadingMicroInverters,
} = useMicroInverters(apiParams);

const {
  handleSort,
  sortedMicroInverters,
  sortState,
} = useTableSorting(() => microInverters.value?.data);

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;
const INPUT_MIN_WIDTHS = {
  BRAND: '140px',
  BUTTONS: '200px',
  ENABLED: '120px',
  HYBRID: '120px',
  MODEL: '140px',
  ORDER_BY: '140px',
  POWER: '160px',
  SKU: '140px',
} as const;

const hasNext = computed(() => !!microInverters.value?.has_next);
const pageSize = computed(() => Number(apiParams.value.page_size) || DEFAULT_PAGE_SIZE);
const currentPage = computed(() => Number(apiParams.value.page) || DEFAULT_PAGE);

function handlePageSizeChange(newPageSize: number): void {
  setPageSize(newPageSize);
}

function handleSetPage(value: number): void {
  const currentPageValue = Number(apiParams.value.page);
  if (hasNext.value || value < currentPageValue) {
    setPage(value);
  }
}

function formatPowerValue(power?: null | number): string {
  return formatValueWithUnit(power, UNITS.KILOWATTS);
}

function formatTensionValue(tension?: null | number): string {
  return formatValueWithUnit(tension, UNITS.VOLTS, DEFAULT_FALLBACK);
}

function formatOverloadValue(overload?: null | number): string {
  return formatValueWithUnit(overload, UNITS.PERCENT);
}

function formatPhaseValue(phaseId?: number): string {
  if (!phaseId) return formatStringValue(undefined);
  const phaseOption = PHASE_OPTIONS.find((option) => option.value === phaseId);
  return phaseOption ? phaseOption.label : formatStringValue(undefined);
}

const isModalVisible = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');
const selectedMicroInverterId = ref<null | number>(null);
const selectedMicroInverterData = ref<MicroInverters.MicroInverter | null>(null);

const handleCreate = () => {
  modalMode.value = 'create';
  selectedMicroInverterId.value = null;
  selectedMicroInverterData.value = null;
  isModalVisible.value = true;
};

const handleView = (microInverter: MicroInverters.MicroInverter) => {
  modalMode.value = 'view';
  selectedMicroInverterId.value = microInverter.id;
  selectedMicroInverterData.value = microInverter;
  isModalVisible.value = true;
};

const handleEdit = (microInverter: MicroInverters.MicroInverter) => {
  modalMode.value = 'edit';
  selectedMicroInverterId.value = microInverter.id;
  selectedMicroInverterData.value = microInverter;
  isModalVisible.value = true;
};

useScrollToTop(currentPage);

onMounted(() => {
  document.title = 'Micro-inversores';
  initializeForm();
});
</script>

<template>
  <DashboardLayout title="Micro-inversores" description="Lista de micro-inversores disponíveis">
    <div class="row row-cards">
      <div class="col-12">
        <div class="card position-relative">
          <div class="card-header d-flex justify-content-end align-items-end">
            <button
              v-permission="[EQUIPMENT_EDIT_ROLE]"
              type="button"
              class="btn btn-primary"
              @click="handleCreate"
            >
              <Plus class="icon icon-sm me-1" />
              {{ EQUIPMENT_BUTTON_LABELS.CREATE_MICROINVERTER }}
            </button>
          </div>
          <div class="card-body border-bottom py-3">
            <div class="d-flex align-items-end gap-2 flex-wrap">
              <div :style="{ minWidth: INPUT_MIN_WIDTHS.BRAND }">
                <div class="mb-3">
                  <label class="form-label" for="brand">{{ EQUIPMENT_LABELS.BRAND }}</label>
                  <input
                    id="brand"
                    v-model="brandModel"
                    v-bind="brandProps"
                    type="text"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.BRAND_FILTER"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.MODEL }">
                <div class="mb-3">
                  <label class="form-label" for="model">{{ EQUIPMENT_LABELS.MODEL }}</label>
                  <input
                    id="model"
                    v-model="modelModel"
                    v-bind="modelProps"
                    type="text"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.MODEL_FILTER"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.SKU }">
                <div class="mb-3">
                  <label class="form-label" for="sku">{{ EQUIPMENT_LABELS.SKUS }}</label>
                  <input
                    id="sku"
                    v-model="skuModel"
                    v-bind="skuProps"
                    type="text"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.SKU_FILTER"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.POWER }">
                <div class="mb-3">
                  <label class="form-label" for="power">{{ EQUIPMENT_LABELS.POWER }}</label>
                  <input
                    id="power"
                    v-model.number="powerModel"
                    v-bind="powerProps"
                    type="number"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.POWER_FILTER"
                    min="0.1"
                    max="999"
                    step="0.1"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.HYBRID }">
                <div class="mb-3">
                  <label class="form-label" for="isHybrid">{{ EQUIPMENT_LABELS.HYBRID }}</label>
                  <select
                    id="isHybrid"
                    v-model="isHybridModel"
                    v-bind="isHybridProps"
                    class="form-select"
                  >
                    <option
                      v-for="option in HYBRID_OPTIONS"
                      :key="String(option.value)"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.ENABLED }">
                <div class="mb-3">
                  <label class="form-label" for="isEnabled">{{ EQUIPMENT_LABELS.STATUS }}</label>
                  <select
                    id="isEnabled"
                    v-model="isEnabledModel"
                    v-bind="isEnabledProps"
                    class="form-select"
                  >
                    <option
                      v-for="option in ENABLED_OPTIONS"
                      :key="String(option.value)"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.ORDER_BY }">
                <div class="mb-3">
                  <label class="form-label" for="orderBy">Ordenar por</label>
                  <select
                    id="orderBy"
                    v-model="orderByModel"
                    v-bind="orderByProps"
                    class="form-select"
                  >
                    <option
                      v-for="option in ORDER_BY_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.BUTTONS }">
                <div class="mb-3 d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="handleSearch"
                  >
                    <Search class="icon icon-sm me-1" />
                    {{ BUTTON_LABELS.SEARCH }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="handleReset"
                  >
                    <FunnelX class="icon icon-sm me-1" />
                    {{ BUTTON_LABELS.CLEAR }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="table-responsive table-with-sticky-actions">
            <table class="table table-vcenter card-table">
              <thead>
                <tr>
                  <th
                    v-for="column in TABLE_COLUMNS.filter(col => col.value !== 'actions')"
                    :key="column.value"
                    :class="{
                      'cursor-pointer': column.sort,
                      'compact-header': column.value === 'count_key' || column.value === 'overload',
                    }"
                    @click="column.sort ? handleSort(column.value as any) : null"
                  >
                    <div class="d-flex align-items-center justify-content-between break-anywhere">
                      <span>{{ column.name }}</span>
                      <div v-if="column.sort" class="d-flex flex-column cursor-pointer border rounded-1 mx-2">
                        <ChevronUp
                          class="icon w-3 h-3"
                          @click="handleSort(column.value as any)"
                          :class="{
                            'text-primary': sortState.column === column.value
                              && sortState.direction === 'asc',
                          }"
                        />
                        <ChevronDown
                          class="icon w-3 h-3"
                          @click="handleSort(column.value as any)"
                          :class="{
                            'text-primary': sortState.column === column.value
                              && sortState.direction === 'desc',
                          }"
                        />
                      </div>
                    </div>
                  </th>
                  <th class="sticky-actions-column">
                    <span>{{ EQUIPMENT_LABELS.ACTIONS }}</span>
                  </th>
                </tr>
              </thead>

              <template v-if="isLoadingMicroInverters">
                <tbody>
                  <tr v-for="(_, index) in range(pageSize + 1)" :key="index">
                    <td v-for="i in range(TABLE_COLUMNS.length)" :key="i">
                      <div class="placeholder-glow" style="height: 19.5px">
                        <span class="placeholder col-12" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>

              <tbody v-else>
                <template v-if="sortedMicroInverters && sortedMicroInverters.length">
                  <tr
                    v-for="microInverter in sortedMicroInverters"
                    :key="microInverter.id"
                  >
                    <td>
                      <span class="text-secondary">{{ formatStringValue(microInverter.brand) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatStringValue(microInverter.model) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatPowerValue(microInverter.power) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary skus-value">{{ formatSkusValue(microInverter.skus) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatTensionValue(microInverter.tension) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatStringValue(microInverter.count_key?.toString()) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatPhaseValue(microInverter.phase_id) }}</span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="microInverter.is_hybrid ? 'bg-info-lt' : 'bg-secondary-lt'"
                      >
                        {{ microInverter.is_hybrid ? STATUS_LABELS.HYBRID : STATUS_LABELS.NON_HYBRID }}
                      </span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="microInverter.is_combo_conecte ? 'bg-success-lt' : 'bg-secondary-lt'"
                      >
                        {{ formatBooleanValue(microInverter.is_combo_conecte) }}
                      </span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatOverloadValue(microInverter.overload) }}</span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="microInverter.is_enabled ? 'bg-success-lt' : 'bg-danger-lt'"
                      >
                        {{ microInverter.is_enabled ? STATUS_LABELS.ENABLED : STATUS_LABELS.DISABLED }}
                      </span>
                    </td>
                    <td class="sticky-actions-column">
                      <div class="d-flex gap-2">
                        <ActionButton
                          type="details"
                          @click="handleView(microInverter)"
                        />
                        <ActionButton
                          v-permission="[EQUIPMENT_EDIT_ROLE]"
                          type="edit"
                          @click="handleEdit(microInverter)"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td :colspan="TABLE_COLUMNS.length">
                      <div class="d-flex justify-content-center">
                        <span>Nenhum micro-inversor encontrado</span>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <Pagination
            :current-page="currentPage"
            :has-next="hasNext"
            :page-size="pageSize"
            @page-change="handleSetPage"
            @page-size-change="handlePageSizeChange"
          />

          <MicroInverterModal
            v-model="isModalVisible"
            :mode="modalMode"
            :micro-inverter-data="selectedMicroInverterData"
            :micro-inverter-id="selectedMicroInverterId"
          />
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
$min-table-height: 50vh;
$hover-opacity: 0.5;
$actions-column-width: 120px;
$actions-column-bg: #ffffff;
$actions-column-border: #dee2e6;

.table-responsive {
  overflow-x: auto;
  overflow-y: auto;

  &.table-with-sticky-actions {
    position: relative;
  }
}

.card {
  .table-responsive {
    min-height: $min-table-height;
    overflow-x: auto;
    overflow-y: auto;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.break-anywhere {
  word-break: break-word;
}

li {
  &:hover {
    opacity: $hover-opacity;
  }
}

.table {
  tbody {
    tr {
      background-color: white !important;

      &:hover {
        background-color: #f8f9fa !important;
      }
    }
  }

  th,
  td {
    white-space: nowrap;
  }

  .compact-header {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
    white-space: normal;
    word-wrap: break-word;
  }

  .skus-value {
    white-space: pre-line;
  }

  .sticky-actions-column {
    position: sticky;
    right: 0;
    background-color: #ffffff;
    border-left: 1px solid $actions-column-border;
    width: $actions-column-width;
    min-width: $actions-column-width;
    max-width: $actions-column-width;
    z-index: 10;

    tr:hover & {
      background-color: #f8f9fa !important;
    }

    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
  }

  thead .sticky-actions-column {
    background-color: var(--tblr-bg-surface-secondary, #f8f9fa);
    border-bottom: 1px solid $actions-column-border;
  }
}
</style>
